import { callApi, API_ENDPOINT } from "./api";

export type AiProcessType = string;

export const SYSTEM_PROCESS_TYPE_ORDER = ["process", "verification", "rectification"] as const;

export function compareProcessTypes(a: string, b: string): number {
  const ai = SYSTEM_PROCESS_TYPE_ORDER.indexOf(a as (typeof SYSTEM_PROCESS_TYPE_ORDER)[number]);
  const bi = SYSTEM_PROCESS_TYPE_ORDER.indexOf(b as (typeof SYSTEM_PROCESS_TYPE_ORDER)[number]);
  const aRank = ai >= 0 ? ai : 100;
  const bRank = bi >= 0 ? bi : 100;
  if (aRank !== bRank) return aRank - bRank;
  return a.localeCompare(b);
}

export type AiDocumentContentType = "word" | "pdf";

export const AI_DOCUMENT_CONTENT_TYPE_OPTIONS: Array<{
  value: AiDocumentContentType;
  label: string;
}> = [
  { value: "word", label: "Word (DOCX text extraction)" },
  { value: "pdf", label: "PDF (convert DOCX to PDF, then extract)" },
];

export interface AiProcessPromptConfig {
  id?: number;
  process_type: string;
  process_label: string;
  is_enabled: number;
  prompt_title: string;
  system_prompt: string;
  user_prompt: string;
  /** word = extract DOCX text; pdf = convert DOCX to PDF first, then extract PDF text */
  document_content_type?: AiDocumentContentType;
  /** When 1, include active EC document text in the OpenAI verification prompt */
  send_ec_document?: number;
  /** Options shown when closing an error for this process. */
  close_remark_options?: string[];
  openai_model: string;
  max_tokens: number;
  temperature: number;
  max_retries: number;
  retry_delay_ms: number;
  is_system: number;
  /** Set when added in UI but not yet saved to the database */
  _isNew?: boolean;
}

export interface AiProcessPromptSaveRow {
  id?: number;
  process_type: string;
  process_label: string;
  is_enabled: number;
  is_system: number;
  prompt_title: string;
  system_prompt: string;
  user_prompt: string;
  document_content_type?: AiDocumentContentType;
  send_ec_document?: number;
  openai_model: string;
  max_tokens: number;
  temperature: number;
  max_retries: number;
  retry_delay_ms: number;
  close_remark_options: string[];
}

export interface AiModelOption {
  id?: number;
  model_id: string;
  model_label: string;
  is_enabled: number;
}

export interface AiApiToolOption {
  id?: number;
  tool_label: string;
  api_endpoint_url: string;
  openai_api_key_masked?: string;
  is_enabled: number;
  _client_key?: string;
}

export interface AiVerificationError {
  id: number;
  run_id: number;
  file_idfk: string;
  file_name: string;
  page_number: string;
  line_number?: string;
  page_line?: string;
  section_field_name: string;
  error_description: string;
  suggested_correction: string;
  severity: string;
  status: "open" | "closed";
  remarks: string;
  process_type: AiProcessType;
  created_at: string;
  closed_at: string | null;
  closed_by: number | null;
  closed_by_name?: string;
  mention1_value: string;
  mention1_section: string;
  mention1_quote: string;
  mention2_value: string;
  mention2_section: string;
  mention2_quote: string;
}

export interface AiWorkflowStatus {
  settings_enabled: number;
  settings_enabled_byprocess?: Record<string, number | string>;
  total_pending: number;
  can_complete: boolean;
  pending_by_process: Record<string, number>;
  latest_run: any;
  latest_run_by_process?: Record<string, any>;
  workflow: any;
}

export function isAiGlobalSettingsEnabled(payload: any): boolean {
  const value = payload?.settings_enabled;
  return Number(value) === 1 || String(value) === "1";
}

export function isAiProcessSettingsEnabled(payload: any, processType: string): boolean {
  const value = payload?.settings_enabled_byprocess?.[processType];
  return Number(value) === 1 || String(value) === "1";
}

/** Verification-list UI: global is_enabled=1, then process verification=1. */
export function isAiVerificationScreenEnabled(payload: any): boolean {
  return isAiGlobalSettingsEnabled(payload) && isAiProcessSettingsEnabled(payload, "verification");
}

/** Work-file UI: global is_enabled=1, then process or rectification by file_status. */
export function isAiWorkFileScreenEnabled(payload: any, filestatus?: string): boolean {
  if (!isAiGlobalSettingsEnabled(payload)) return false;
  if (String(filestatus ?? "").trim() === "9") {
    return isAiProcessSettingsEnabled(payload, "rectification");
  }
  return isAiProcessSettingsEnabled(payload, "process");
}

export const AI_RESUBMIT_REQUIRED_COMPLETE_MESSAGE =
  "Finish the verification before proceed to completete the file";

export const AI_NO_RUN_COMPLETE_MESSAGE =
  '"With out Run Verication" You can\'t complete this file';

export function isAiRunActionResubmitRequired(latestRun: any): boolean {
  return String(latestRun?.action || "").toLowerCase() === "resubmit_required";
}

export function isAiLatestRunMissing(latestRun: any): boolean {
  if (!latestRun) return true;
  const noRecord = latestRun.no_record;
  return (
    noRecord === true ||
    noRecord === 1 ||
    String(noRecord).toLowerCase() === "true" ||
    String(noRecord) === "1"
  );
}

export const AI_VERIFICATION_IN_PROGRESS_COMPLETE_MESSAGE =
  "Verication is in Progress,can not complete the file";

export function getAiWorkflowPayload(response: any): any {
  if (!response) return null;
  if (
    response.data &&
    (response.data.latest_run !== undefined ||
      response.data.settings_enabled !== undefined)
  ) {
    return response.data;
  }
  if (
    response.latest_run !== undefined ||
    response.settings_enabled !== undefined
  ) {
    return response;
  }
  return response.data || null;
}

export function isAiVerificationStatusInProgress(latestRun: any): boolean {
  if (!latestRun) return false;
  const status = latestRun.verification_status;
  if (status === undefined || status === null || status === "") return false;
  return String(status).trim() === "0";
}

export const AI_RERUN_VERIFICATION_COMPLETE_MESSAGE =
  "Please run the verification to review and resolve any system verification errors before completing";

export function isAiVerificationStatusNeedsRerun(latestRun: any): boolean {
  if (!latestRun) return false;
  const status = String(latestRun.verification_status ?? "").trim();
  const action = String(latestRun.action || "").toLowerCase();
  return status === "1" && action !== "completed";
}

export function formatAiVerificationErrorTitleMessage(response: any): string | null {
  if (!response) return null;
  const source =
    response.data && response.data.error_title !== undefined && response.error_title === undefined
      ? response.data
      : response;
  const title = String(source.error_title ?? "").trim();
  if (!title) return null;
  const message = String(source.message ?? "").trim();
  if (title && message) return `${title}: ${message}. Please try again`;
  return title;
}

export type AiVerificationContext = "work-file" | "verification-list";

/** /work-file: status 9 → rectification; not 9 and not 6 → process; status 6 → unavailable */
export function resolveProcessTypeForWorkFile(filestatus: string): string | null {
  const fs = String(filestatus ?? "").trim();
  if (fs === "9") return "rectification";
  if (fs === "6") return null;
  return "process";
}

/** /work-file-verification-list: status 5 (completed) → unavailable; else verification */
export function resolveProcessTypeForVerificationList(filestatus: string): string | null {
  const fs = String(filestatus ?? "").trim();
  if (fs === "5") return null;
  return "verification";
}

export function resolveProcessTypeForContext(
  context: AiVerificationContext,
  filestatus: string,
): string | null {
  return context === "verification-list"
    ? resolveProcessTypeForVerificationList(filestatus)
    : resolveProcessTypeForWorkFile(filestatus);
}

/** Complete-file AI gates follow the same process as screen enablement, not run-verification availability. */
export function resolveProcessTypeForCompleteGate(
  context: AiVerificationContext,
  filestatus: string,
): string {
  if (context === "verification-list") {
    return "verification";
  }
  return String(filestatus ?? "").trim() === "9" ? "rectification" : "process";
}

export function pickLatestRunForProcess(
  payload: any,
  processType?: string | null,
): any {
  const emptyRun = { no_record: true };
  if (!payload) return emptyRun;
  const key = String(processType || "").trim();
  const byProcess = payload.latest_run_by_process;
  if (byProcess && typeof byProcess === "object") {
    if (!key) return emptyRun;
    const run = byProcess[key];
    if (run == null) return emptyRun;
    return run;
  }
  return payload.latest_run ?? emptyRun;
}

export type AiCompleteFileBlock = {
  message: string;
  toastType: "error" | "success";
};

/** Conditions that apply only after the process setting gate is on. */
export function getAiCompleteFileBlock(
  payload: any,
  processType: string,
  pendingMessage?: (pendingCount: number) => string,
): AiCompleteFileBlock | null {
  const latestRun = pickLatestRunForProcess(payload, processType);
  if (isAiLatestRunMissing(latestRun)) {
    return { message: AI_NO_RUN_COMPLETE_MESSAGE, toastType: "error" };
  }
  if (isAiVerificationStatusInProgress(latestRun)) {
    return { message: AI_VERIFICATION_IN_PROGRESS_COMPLETE_MESSAGE, toastType: "error" };
  }
  if (isAiVerificationStatusNeedsRerun(latestRun)) {
    return { message: AI_RERUN_VERIFICATION_COMPLETE_MESSAGE, toastType: "error" };
  }
  if (isAiRunActionResubmitRequired(latestRun)) {
    return { message: AI_RESUBMIT_REQUIRED_COMPLETE_MESSAGE, toastType: "success" };
  }
  const pendingCount = Number(payload?.pending_by_process?.[processType]) || 0;
  if (pendingCount > 0) {
    const message = pendingMessage
      ? pendingMessage(pendingCount)
      : `Complete ${pendingCount} open system verification error(s) with remarks before completing the file.`;
    return { message, toastType: "error" };
  }
  return null;
}

/** Run Verification is allowed only when the file is taken for the active stage. */
export function isFileTakenForProcessType(
  processType: string,
  takenStatus?: number | string | null,
  vtakenStatus?: number | string | null,
  rtakenStatus?: number | string | null,
): boolean {
  const isTaken = (value?: number | string | null) => Number(value) === 1;
  if (processType === "verification") return isTaken(vtakenStatus);
  if (processType === "rectification") return isTaken(rtakenStatus);
  return isTaken(takenStatus);
}

/** Frontend upload flow when download is allowed or opinion type is not server-only. */
export function requiresAiVerificationDocumentUpload(
  blockDownloadStatus?: string | boolean | null,
  fileOpinionType?: string | number | null,
): boolean {
  const blockDownload = String(blockDownloadStatus) === "true";
  const opinionType = String(fileOpinionType ?? "");
  return !blockDownload || opinionType !== "1";
}

export async function uploadAiVerificationDocument(payload: {
  file_idfk: string;
  file: string;
  file_name: string;
  file_ext?: string;
  userId?: number | string;
}) {
  const res = await callApi("POST", "ai_verification_document_upload.php", payload);
  return res.data;
}

/** @deprecated Prefer resolveProcessTypeForContext */
export function resolveProcessType(filestatus: string): string {
  return (
    resolveProcessTypeForWorkFile(filestatus) ||
    resolveProcessTypeForVerificationList(filestatus) ||
    "process"
  );
}

export async function getAiVerificationApiTools() {
  const res = await callApi("GET", "ai_verification_api_tools_get.php");
  return res.data;
}

export interface AiApiToolSaveRow {
  id?: number;
  tool_label: string;
  api_endpoint_url: string;
  openai_api_key?: string;
  is_enabled: number;
  _client_key?: string;
}

export async function saveAiVerificationApiTools(payload: {
  tools: AiApiToolSaveRow[];
  active_api_tool_id?: number;
  active_client_key?: string;
  updated_by?: number | string;
}) {
  const res = await callApi("POST", "ai_verification_api_tools_save.php", payload);
  return res.data;
}

export async function getAiVerificationModels() {
  const res = await callApi("GET", "ai_verification_models_get.php");
  return res.data;
}

export async function saveAiVerificationPrompts(payload: {
  prompts: AiProcessPromptSaveRow[];
  updated_by?: number | string;
}) {
  const res = await callApi("POST", "ai_verification_prompts_save.php", payload);
  return res.data;
}

export async function saveAiVerificationModels(payload: {
  models: Array<{
    id?: number;
    model_id: string;
    model_label: string;
    is_enabled: number;
  }>;
  updated_by?: number | string;
}) {
  const res = await callApi("POST", "ai_verification_models_save.php", payload);
  return res.data;
}

export async function deleteAiVerificationProcess(processType: string, userId?: number) {
  const res = await callApi("POST", "ai_verification_process_delete.php", {
    process_type: processType,
    userId,
  });
  return res.data;
}

export async function getAiVerificationSettings() {
  const res = await callApi("GET", "ai_verification_settings_get.php");
  return res.data;
}

export interface AiGlobalSettingsSavePayload {
  is_enabled: number;
  active_api_tool_id?: number;
  openai_model: string;
  max_retries: number;
  retry_delay_ms: number;
  max_tokens: number;
  temperature: number;
  updated_by?: number | string;
}

export async function saveAiVerificationSettings(payload: AiGlobalSettingsSavePayload) {
  const res = await callApi("POST", "ai_verification_settings_save.php", payload);
  return res.data;
}

export async function getAiVerificationPrompts() {
  const res = await callApi("GET", "ai_verification_prompts_get.php");
  return res.data;
}

export async function runAiVerification(payload: {
  file_idfk: string;
  process_type: string;
  document_url?: string;
  document_path?: string;
  file_name?: string;
  userId?: number;
}) {
  const res = await callApi("POST", "ai_verification_run.php", payload);
  return res.data;
}

export async function getAiVerificationErrors(payload: {
  file_idfk?: string;
  process_type?: string | "";
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
}) {
  const res = await callApi("POST", "ai_verification_errors_get.php", payload);
  return res.data;
}

export async function updateAiVerificationError(payload: {
  error_id: number;
  status: "open" | "closed";
  remarks?: string;
  userId?: number;
}) {
  const res = await callApi("POST", "ai_verification_error_update.php", payload);
  return res.data;
}

export async function getAiWorkflowStatus(fileId: string, processType?: string | null) {
  const payload: { file_idfk: string; process_type?: string } = { file_idfk: fileId };
  const key = String(processType || "").trim();
  if (key) payload.process_type = key;
  const res = await callApi("POST", "ai_verification_workflow_get.php", payload);
  return res.data;
}

export async function getAiVerificationReports(payload: any) {
  const res = await callApi("POST", "ai_verification_reports.php", payload);
  return res.data;
}

export function exportAiVerificationErrors(payload: {
  file_idfk?: string;
  process_type?: string;
  status?: string;
  format: "csv" | "word";
}) {
  const authKey = localStorage.getItem("authkey");
  return fetch(API_ENDPOINT + "ai_verification_export.php", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authKey}`,
      "Content-Type": "application/json",
      Origin: window.location.origin,
    },
    body: JSON.stringify(payload),
  });
}

export const DEFAULT_CLOSE_REMARK_OPTIONS = [
  "Corrected",
  "No Need",
];

/** @deprecated Use getCloseRemarkOptionsForProcess */
export const ERROR_CLOSE_REMARK_OPTIONS = DEFAULT_CLOSE_REMARK_OPTIONS;

export function normalizeCloseRemarkOptions(raw: unknown): string[] {
  if (raw == null) return [];

  if (typeof raw === "string") {
    const text = raw.trim();
    if (!text) return [];
    if (text.startsWith("[")) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return normalizeCloseRemarkOptions(parsed);
        }
      } catch {
        /* treat as plain text below */
      }
    }
    return text
      .split(/[\r\n,]+/)
      .map((v) => v.trim())
      .filter((v) => v !== "");
  }

  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter((v) => v !== "");
}

export function getCloseRemarkOptionsForProcess(
  prompts: AiProcessPromptConfig[] | null | undefined,
  processType: string
): string[] {
  const key = (processType || "").trim().toLowerCase();
  const row = (prompts || []).find(
    (p) => (p.process_type || "").trim().toLowerCase() === key,
  );
  const options = normalizeCloseRemarkOptions(row?.close_remark_options);
  return options.length ? options : [...DEFAULT_CLOSE_REMARK_OPTIONS];
}

export function formatPageLineDisplay(
  pageNumber?: string | null,
  lineNumber?: string | null,
): string {
  let page = String(pageNumber || "").trim();
  let line = String(lineNumber || "").trim();

  if (!line && page) {
    const combined = page.match(/^(\d+)\s*[\/-]\s*(\d+)$/);
    if (combined) {
      page = combined[1];
      line = combined[2];
    } else {
      const labeled = page.match(/page\s*(\d+).*line\s*(\d+)/i);
      if (labeled) {
        page = labeled[1];
        line = labeled[2];
      }
    }
  }

  if (page && line) return `${page} / ${line}`;
  if (page) return page;
  if (line) return line;
  return "—";
}

export function severityBadgeClass(severity: string): string {
  const s = (severity || "").toLowerCase();
  if (s === "critical" || s === "high") return "badge-danger";
  if (s === "medium") return "badge-warning";
  return "badge-info";
}
